import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SelectorLabelBindingDirective } from "@components/selector/selector-label-binding.directive";
import { SelectorValueBindingDirective } from "@components/selector/selector-value-binding.directive";
import { SelectorComponent } from "@components/selector/selector.component";
import _ from "lodash";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";
import { SkogsvardAtgardsGrupp, SkogsvardKornstorlek, SkogsvardMarkfuktighet, SkogsvardSkonsamMarkbehandling } from "~app/common/models/constants";
import { canEdit } from "~app/role-permissions";
import { Atgard } from "../../models/atgard-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
@Component({
    selector: "rask-skogsvard-markbehandling-standort",
    templateUrl: "./skogsvard-markbehandling-standort.component.html",
    styleUrls: ["./skogsvard-markbehandling-standort.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        SelectorLabelBindingDirective,
        SelectorValueBindingDirective,
        FormsModule,
        RadiobuttonGroupComponent
    ]
})
export class SkogsvardMarkbehandlingStandortComponent implements OnInit, OnDestroy, AfterViewInit {
    protected barighetItems: SimpleSelectionItem[] = [
        {
            id: "1",
            value:1,
            text:"1 Mycket god"
        },
        {
            id: "2",
            value:2,
            text:"2 God"
        },
        {
            id: "3",
            value:3,
            text:"3 Medelgod"
        },
        {
            id: "4",
            value:4,
            text:"4 Dålig"
        },
        {
            id: "5",
            value:5,
            text:"5 Mycket dålig"
        }
    ];
    protected kontraktReadonly = false;
    protected kornstorlekItems: SimpleSelectionItem[] = [
        {
            id: SkogsvardKornstorlek.grov,
            value: SkogsvardKornstorlek.grov,
            text: SkogsvardKornstorlek.grov
        },
        {
            id: SkogsvardKornstorlek.medel,
            value: SkogsvardKornstorlek.medel,
            text: SkogsvardKornstorlek.medel
        },
        {
            id: SkogsvardKornstorlek.fin,
            value: SkogsvardKornstorlek.fin,
            text: SkogsvardKornstorlek.fin
        }
    ];
    protected lutningItems: SimpleSelectionItem[] = [
        {
            id: "1",
            value:1,
            text:"1 Lutning < 5%"
        },
        {
            id: "2",
            value:2,
            text:"2 Lutning > 5% < 15%"
        },
        {
            id: "3",
            value:3,
            text:"3 Lutning > 15% < 34%"
        },
        {
            id: "4",
            value:4,
            text:"4 Lutning > 35% < 50%"
        },
        {
            id: "5",
            value:5,
            text:"5 Lutning > 50%"
        }
    ];
    protected markfuktighetItems: SimpleSelectionItem[] = [
        {
            id: SkogsvardMarkfuktighet.torr,
            value: SkogsvardMarkfuktighet.torr,
            text: SkogsvardMarkfuktighet.torr
        },
        {
            id: SkogsvardMarkfuktighet.frisk,
            value: SkogsvardMarkfuktighet.frisk,
            text: SkogsvardMarkfuktighet.frisk
        },
        {
            id: SkogsvardMarkfuktighet.fuktig,
            value: SkogsvardMarkfuktighet.fuktig,
            text: SkogsvardMarkfuktighet.fuktig
        },
        {
            id: SkogsvardMarkfuktighet.blot,
            value: SkogsvardMarkfuktighet.blot,
            text: SkogsvardMarkfuktighet.blot
        }
    ];
    protected skogsvardsAtgard: Atgard;
    protected skonsamMarkbehandlingRadioButtons: IRadiobuttonItem[] = [
        { key: SkogsvardSkonsamMarkbehandling.nej,
            value: SkogsvardSkonsamMarkbehandling.nej },
        { key: SkogsvardSkonsamMarkbehandling.ja20,
            value: SkogsvardSkonsamMarkbehandling.ja20 },
        { key: SkogsvardSkonsamMarkbehandling.ja40,
            value: SkogsvardSkonsamMarkbehandling.ja40 }
    ];
    protected ytstrukturItems: SimpleSelectionItem[] = [
        {
            id: "1",
            value:1,
            text:"1 Ytblock saknas"
        },
        {
            id: "2",
            value:2,
            text:"2 Liten förekomst"
        },
        {
            id: "3",
            value:3,
            text:"3 Måttlig förekomst"
        },
        {
            id: "4",
            value:4,
            text:"4 Hög förekomst"
        },
        {
            id: "5",
            value:5,
            text:"5 Mycket hög förekomst"
        }
    ];

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();

    private isLoading = true;

    private readonly canEdit = canEdit();

    constructor() {
        this.skogsvardsAtgard = new Atgard();
    }

    ngAfterViewInit(): void {
        window.requestAnimationFrame(() => {
            this.isLoading = false;
        });
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .subscribe(atgard => this.skogsvardsAtgard = atgard);

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.ngDestroyed$))
            .subscribe(readonly => this.kontraktReadonly = readonly);
    }

    protected onMarkbehandlingChange($event) {
        if(_.isNil($event)) {
            return;
        }

        if(!this.isLoading) {
            this.atgardMediatorService.update(this.skogsvardsAtgard);
        }
    }

    protected showSkonsam() {
        return this.skogsvardsAtgard?.atgardsgrupp === SkogsvardAtgardsGrupp.markberedning;
    }
}
